import {Ionicons} from '@expo/vector-icons';
import {observer} from 'mobx-react-lite';
import {useMemo} from 'react';
import type {Insets, ViewStyle} from 'react-native';
import {Pressable, StyleSheet, Text, View} from 'react-native';

import ContentScrollView from '../components/ContentScrollView';
import FixedWidthRowView from '../components/FixedWidthRowView';
import {RussianDayOfTheWeek} from '../i18n/RussianDayOfTheWeek';
import {useTheme, variance} from '../styling';
import type {ScheduleIndex} from '../SupportGroups/ScheduleIndex';
import {DayOfTheWeek} from '../util/DayOfTheWeek';

export type ShowGroupScreenProps = {
  street?: string;
  house?: string;
  comment?: string | null;
  entrance?: string | null;
  intercomCode?: string | null;
  floor?: string | null;
  apartmentOrOffice?: string | null;
  schedule?: ScheduleIndex | null;
  localTime?: string;
  onAddressPress?: () => void;
  compensateHeaderHeight?: number;
};

export default observer(function ShowGroupScreen(props: ShowGroupScreenProps) {
  const {
    street,
    house,
    apartmentOrOffice,
    entrance,
    floor,
    intercomCode,
    comment,
    schedule,
    localTime,
    onAddressPress,
    compensateHeaderHeight,
  } = props;

  const theme = useTheme();

  const contentContainerStyle: ViewStyle = useMemo(
    () => ({
      paddingTop: compensateHeaderHeight ?? 0,
    }),
    [compensateHeaderHeight],
  );
  const topIsCompensated = compensateHeaderHeight !== undefined;

  return (
    <ContentScrollView
      contentContainerStyle={contentContainerStyle}
      rootStyle={layoutStyles.root}
      topIsCompensated={topIsCompensated}>
      <FixedWidthRowView innerStyle={layoutStyles.addressBlock}>
        {street !== undefined && house !== undefined && (
          <Pressable
            style={layoutStyles.row}
            hitSlop={HIT_SLOP}
            onPress={onAddressPress}>
            <AddressText style={layoutStyles.shrink}>
              <AddressText title>Улица и дом: </AddressText>
              {street} {house}
            </AddressText>
            <Ionicons
              style={layoutStyles.noShrink}
              name="copy-outline"
              size={24}
              color={theme.palette.textPrimary}
            />
          </Pressable>
        )}
        {!!comment && (
          <AddressText>
            <AddressText title>Комментарий: </AddressText>
            {comment}
          </AddressText>
        )}
        {!!entrance && (
          <AddressText>
            <AddressText title>Подъезд или вход: </AddressText>
            {entrance}
          </AddressText>
        )}
        {!!intercomCode && (
          <AddressText>
            <AddressText title>Код домофона: </AddressText>
            {intercomCode}
          </AddressText>
        )}
        {!!floor && (
          <AddressText>
            <AddressText title>Этаж: </AddressText> {floor}
          </AddressText>
        )}
        {!!apartmentOrOffice && (
          <AddressText>
            <AddressText title>Апартаменты или офис: </AddressText>
            {apartmentOrOffice}
          </AddressText>
        )}
        {!!localTime && (
          <AddressText>
            <AddressText title>Местное время: </AddressText>
            {localTime}
          </AddressText>
        )}
      </FixedWidthRowView>
      {schedule === null ? (
        <FixedWidthRowView>
          <Subtitle>У этой группы пока нет собраний</Subtitle>
        </FixedWidthRowView>
      ) : schedule === undefined ? undefined : (
        <FixedWidthRowView>
          <Subtitle>Расписание собраний</Subtitle>
          <ScheduleView schedule={schedule} />
        </FixedWidthRowView>
      )}
    </ContentScrollView>
  );
});

const HIT_SLOP = {top: 16, left: 16, bottom: 16, right: 16} satisfies Insets;

const AddressText = variance(Text)(theme => ({
  root: {
    ...theme.text('base'),
    color: theme.palette.textPrimary,
    fontSize: 16,
    lineHeight: 18,
  },
  title: {
    ...theme.text('header5'),
  },
}));

const Subtitle = variance(Text)(theme => ({
  root: {
    alignSelf: 'center',
    ...theme.text('header5'),
    color: theme.palette.textPrimary,
    fontSize: 16,
    lineHeight: 18,
    textAlign: 'center',
  },
}));

type ScheduleViewProps = {
  schedule: ScheduleIndex;
};

const ScheduleView = observer(function ScheduleView(props: ScheduleViewProps) {
  const {schedule} = props;
  return (
    <ScheduleRootView>
      {DAY_ORDER.map(day => {
        const map = schedule[day];
        if (!map) {
          return null;
        }
        return (
          <View style={layoutStyles.dayContent} key={day}>
            <CellText header style={layoutStyles.cell}>
              {RussianDayOfTheWeek[day]}
            </CellText>
            {[...map.keys()].map(start => (
              <CellText key={start} style={layoutStyles.cell}>
                {start}
              </CellText>
            ))}
          </View>
        );
      })}
    </ScheduleRootView>
  );
});

const DAY_ORDER = [
  DayOfTheWeek.Monday,
  DayOfTheWeek.Tuesday,
  DayOfTheWeek.Wednesday,
  DayOfTheWeek.Thursday,
  DayOfTheWeek.Friday,
  DayOfTheWeek.Saturday,
  DayOfTheWeek.Sunday,
];

const ScheduleRootView = variance(View)(theme => ({
  root: {
    padding: 12,
    borderRadius: 11,
    backgroundColor: theme.palette.backgroundAverage,
    gap: 24,
  },
}));

const layoutStyles = StyleSheet.create({
  root: {
    rowGap: 24,
    paddingBottom: 24,
  },
  addressBlock: {
    gap: 16,
  },
  dayContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    columnGap: 16,
  },
  cell: {
    flexShrink: 0,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 8,
  },
  noShrink: {
    flexShrink: 0,
    flexGrow: 0,
  },
  shrink: {
    flexShrink: 1,
    flexGrow: 1,
  },
});

const CellText = variance(Text)(theme => ({
  root: {
    ...theme.text('base'),
    color: theme.palette.textPrimary,
    fontSize: 16,
    lineHeight: 28,
    textAlign: 'center',
  },
  header: {
    ...theme.text('header6'),
  },
}));
